import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Avatar,
  Descriptions,
  Badge,
  Drawer,
  List,
  Empty,
  Spin,
  Image,
} from 'antd';
import {
  PlusOutlined,
  QrcodeOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined,
  HistoryOutlined,
  ToolOutlined,
  CrownOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface Split {
  id: number;
  split_code: string;
  qr_code_url: string | null;
  customer_id: number;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  power: string | null;
  power_type: string;
  location: string | null;
  floor: string | null;
  installation_date: string | null;
  warranty_end_date: string | null;
  last_maintenance_date: string | null;
  next_maintenance_date: string | null;
  status: string;
  notes: string | null;
  photo_url: string | null;
  intervention_count: number;
  installation_address: string | null;
  created_at: string;
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  subscriptions?: Array<{
    id: number;
    status: string;
    offer?: {
      id: number;
      title: string;
    };
  }>;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const SplitsPage: React.FC = () => {
  const [splits, setSplits] = useState<Split[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedSplit, setSelectedSplit] = useState<Split | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCustomer, setFilterCustomer] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [form] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    withOffer: 0,
    needsMaintenance: 0,
  });

  // Charger les splits
  const fetchSplits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      if (searchText) params.search = searchText;
      if (filterStatus) params.status = filterStatus;
      if (filterCustomer) params.customerId = filterCustomer;

      const response = await api.get('/splits', { params });
      if (response.data.success) {
        setSplits(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || response.data.data.length,
        }));

        // Calculer les stats
        const data = response.data.data;
        setStats({
          total: data.length,
          active: data.filter((s: Split) => s.status === 'active').length,
          withOffer: data.filter((s: Split) => 
            s.subscriptions?.some(sub => sub.status === 'active')
          ).length,
          needsMaintenance: data.filter((s: Split) => {
            if (!s.next_maintenance_date) return false;
            return dayjs(s.next_maintenance_date).isBefore(dayjs().add(30, 'days'));
          }).length,
        });
      }
    } catch (error) {
      console.error('Erreur chargement splits:', error);
      message.error('Erreur lors du chargement des splits');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, filterStatus, filterCustomer]);

  // Charger les clients
  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      if (response.data.success) {
        // L'API retourne { success: true, data: { customers: [...], total, page, ... } }
        const data = response.data.data?.customers || response.data.data || [];
        setCustomers(Array.isArray(data) ? data : []);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Erreur chargement clients:', error);
      setCustomers([]);
    }
  };

  useEffect(() => {
    fetchSplits();
  }, [fetchSplits]);

  // Charger les clients une seule fois au montage
  useEffect(() => {
    fetchCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Créer/Modifier un split
  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        installation_date: values.installation_date?.format('YYYY-MM-DD'),
        warranty_end_date: values.warranty_end_date?.format('YYYY-MM-DD'),
      };

      if (editingId) {
        await api.put(`/splits/${editingId}`, data);
        message.success('Split mis à jour avec succès');
      } else {
        await api.post('/splits', data);
        message.success('Split créé avec succès');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchSplits();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  // Supprimer un split
  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/splits/${id}`);
      message.success('Split supprimé avec succès');
      fetchSplits();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // Régénérer le QR code
  const handleRegenerateQR = async (id: number) => {
    try {
      await api.post(`/splits/${id}/regenerate-qr`);
      message.success('QR code régénéré avec succès');
      fetchSplits();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de la régénération');
    }
  };

  // Ouvrir le modal d'édition
  const openEditModal = (split: Split) => {
    setEditingId(split.id);
    form.setFieldsValue({
      ...split,
      installation_date: split.installation_date ? dayjs(split.installation_date) : null,
      warranty_end_date: split.warranty_end_date ? dayjs(split.warranty_end_date) : null,
    });
    setModalVisible(true);
  };

  // Voir les détails
  const openDetailDrawer = async (split: Split) => {
    try {
      const response = await api.get(`/splits/${split.id}`);
      if (response.data.success) {
        setSelectedSplit(response.data.data);
        setDetailDrawerVisible(true);
      }
    } catch (error) {
      message.error('Erreur lors du chargement des détails');
    }
  };

  // Télécharger le QR code
  const downloadQRCode = (split: Split) => {
    if (split.qr_code_url) {
      const link = document.createElement('a');
      link.href = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${split.qr_code_url}`;
      link.download = `QR_${split.split_code}.png`;
      link.click();
    }
  };

  // Colonnes du tableau
  const columns: ColumnsType<Split> = [
    {
      title: 'Code',
      dataIndex: 'split_code',
      key: 'split_code',
      width: 180,
      render: (code: string, record) => (
        <Space>
          {record.qr_code_url && (
            <Tooltip title="QR Code disponible">
              <QrcodeOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          )}
          <Text code copyable>{code}</Text>
        </Space>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      width: 200,
      render: (customer: any) => customer ? (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <div>
            <div>{customer.first_name} {customer.last_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{customer.phone}</Text>
          </div>
        </Space>
      ) : '-',
    },
    {
      title: 'Équipement',
      key: 'equipment',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.brand} {record.model}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.power} {record.power_type}
          </Text>
        </div>
      ),
    },
    {
      title: 'Localisation',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.location || '-'}</div>
          {record.floor && <Text type="secondary" style={{ fontSize: 12 }}>{record.floor}</Text>}
        </div>
      ),
    },
    {
      title: 'Offre',
      key: 'offer',
      width: 130,
      render: (_, record) => {
        const activeOffer = record.subscriptions?.find(s => s.status === 'active');
        return activeOffer ? (
          <Tag color="gold" icon={<CrownOutlined />}>
            {activeOffer.offer?.title || 'Offre active'}
          </Tag>
        ) : (
          <Tag>Aucune offre</Tag>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; label: string }> = {
          active: { color: 'green', label: 'Actif' },
          inactive: { color: 'default', label: 'Inactif' },
          out_of_service: { color: 'red', label: 'Hors service' },
          pending_installation: { color: 'blue', label: 'Installation en attente' },
        };
        const { color, label } = statusMap[status] || { color: 'default', label: status };
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Interventions',
      dataIndex: 'intervention_count',
      key: 'intervention_count',
      width: 100,
      align: 'center',
      render: (count: number) => (
        <Badge count={count} showZero style={{ backgroundColor: count > 0 ? '#1890ff' : '#d9d9d9' }} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => openDetailDrawer(record)}
            />
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          {record.qr_code_url && (
            <Tooltip title="Télécharger QR">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={() => downloadQRCode(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Régénérer QR">
            <Button
              type="text"
              icon={<QrcodeOutlined />}
              onClick={() => handleRegenerateQR(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer ce split ?"
            description="Cette action est irréversible si le split n'a pas d'historique."
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <ToolOutlined /> Gestion des Splits
          </Title>
          <Text type="secondary">
            Traçabilité des équipements par QR code
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nouveau Split
          </Button>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Splits"
              value={stats.total}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Actifs"
              value={stats.active}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avec Offre"
              value={stats.withOffer}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Entretien à prévoir"
              value={stats.needsMaintenance}
              valueStyle={{ color: stats.needsMaintenance > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="Rechercher (code, marque, modèle...)"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => fetchSplits()}
              allowClear
            />
          </Col>
          <Col span={5}>
            <Select
              placeholder="Statut"
              style={{ width: '100%' }}
              allowClear
              value={filterStatus || undefined}
              onChange={(value) => setFilterStatus(value || '')}
            >
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
              <Option value="out_of_service">Hors service</Option>
              <Option value="pending_installation">Installation en attente</Option>
            </Select>
          </Col>
          <Col span={7}>
            <Select
              placeholder="Filtrer par client"
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filterCustomer || undefined}
              onChange={(value) => setFilterCustomer(value || null)}
            >
              {customers.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name} - {c.phone}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Space>
              <Button onClick={() => fetchSplits()} icon={<SearchOutlined />}>
                Rechercher
              </Button>
              <Button
                onClick={() => {
                  setSearchText('');
                  setFilterStatus('');
                  setFilterCustomer(null);
                }}
              >
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={splits}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `${total} split(s)`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            },
          }}
        />
      </Card>

      {/* Modal Création/Édition */}
      <Modal
        title={editingId ? 'Modifier le Split' : 'Nouveau Split'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="customer_id"
                label="Client"
                rules={[{ required: true, message: 'Veuillez sélectionner un client' }]}
              >
                <Select
                  placeholder="Sélectionner un client"
                  showSearch
                  optionFilterProp="children"
                >
                  {customers.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.first_name} {c.last_name} - {c.phone}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="brand" label="Marque">
                <Input placeholder="Ex: Samsung, LG, Daikin..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label="Modèle">
                <Input placeholder="Ex: AR12TXHQASINEU" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="power" label="Puissance">
                <Input placeholder="Ex: 12000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="power_type" label="Unité" initialValue="BTU">
                <Select>
                  <Option value="BTU">BTU</Option>
                  <Option value="kW">kW</Option>
                  <Option value="CV">CV</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="serial_number" label="N° de série">
                <Input placeholder="Numéro de série fabricant" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Statut" initialValue="active">
                <Select>
                  <Option value="active">Actif</Option>
                  <Option value="inactive">Inactif</Option>
                  <Option value="out_of_service">Hors service</Option>
                  <Option value="pending_installation">Installation en attente</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label="Pièce / Localisation">
                <Input placeholder="Ex: Salon, Chambre 1, Bureau..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="floor" label="Étage">
                <Input placeholder="Ex: RDC, 1er étage..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="installation_date" label="Date d'installation">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="warranty_end_date" label="Fin de garantie">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="installation_address" label="Adresse d'installation (si différente)">
            <Input.TextArea rows={2} placeholder="Laisser vide si identique à l'adresse du client" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Notes supplémentaires..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                {editingId ? 'Mettre à jour' : 'Créer le Split'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer Détails */}
      <Drawer
        title={
          <Space>
            <QrcodeOutlined />
            Détails du Split
          </Space>
        }
        placement="right"
        width={600}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false);
          setSelectedSplit(null);
        }}
        extra={
          <Space>
            {selectedSplit?.qr_code_url && (
              <Button
                icon={<PrinterOutlined />}
                onClick={() => selectedSplit && downloadQRCode(selectedSplit)}
              >
                Imprimer QR
              </Button>
            )}
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                if (selectedSplit) {
                  setDetailDrawerVisible(false);
                  openEditModal(selectedSplit);
                }
              }}
            >
              Modifier
            </Button>
          </Space>
        }
      >
        {selectedSplit && (
          <div>
            {/* QR Code */}
            {selectedSplit.qr_code_url && (
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Image
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${selectedSplit.qr_code_url}`}
                  alt="QR Code"
                  width={200}
                  style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}
                />
                <div style={{ marginTop: 8 }}>
                  <Text code copyable style={{ fontSize: 16 }}>
                    {selectedSplit.split_code}
                  </Text>
                </div>
              </div>
            )}

            {/* Infos client */}
            <Card size="small" title="Client" style={{ marginBottom: 16 }}>
              {selectedSplit.customer ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Nom">
                    {selectedSplit.customer.first_name} {selectedSplit.customer.last_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Téléphone">
                    {selectedSplit.customer.phone}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedSplit.customer.email}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Text type="secondary">Client non défini</Text>
              )}
            </Card>

            {/* Infos techniques */}
            <Card size="small" title="Informations techniques" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Marque">
                  {selectedSplit.brand || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Modèle">
                  {selectedSplit.model || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="N° Série">
                  {selectedSplit.serial_number || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Puissance">
                  {selectedSplit.power} {selectedSplit.power_type}
                </Descriptions.Item>
                <Descriptions.Item label="Localisation">
                  {selectedSplit.location || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Étage">
                  {selectedSplit.floor || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Dates */}
            <Card size="small" title="Dates" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Installation">
                  {selectedSplit.installation_date
                    ? dayjs(selectedSplit.installation_date).format('DD/MM/YYYY')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Fin garantie">
                  {selectedSplit.warranty_end_date
                    ? dayjs(selectedSplit.warranty_end_date).format('DD/MM/YYYY')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Dernier entretien">
                  {selectedSplit.last_maintenance_date
                    ? dayjs(selectedSplit.last_maintenance_date).format('DD/MM/YYYY')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Prochain entretien">
                  {selectedSplit.next_maintenance_date
                    ? dayjs(selectedSplit.next_maintenance_date).format('DD/MM/YYYY')
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Offre active */}
            <Card
              size="small"
              title={
                <Space>
                  <CrownOutlined style={{ color: '#faad14' }} />
                  Offre active
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              {selectedSplit.subscriptions?.find(s => s.status === 'active') ? (
                <Tag color="gold" style={{ fontSize: 14, padding: '4px 12px' }}>
                  {selectedSplit.subscriptions.find(s => s.status === 'active')?.offer?.title}
                </Tag>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aucune offre active sur ce split"
                />
              )}
            </Card>

            {/* Notes */}
            {selectedSplit.notes && (
              <Card size="small" title="Notes" style={{ marginBottom: 16 }}>
                <Paragraph>{selectedSplit.notes}</Paragraph>
              </Card>
            )}

            {/* Stats */}
            <Card size="small" title="Statistiques">
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Interventions"
                    value={selectedSplit.intervention_count}
                    prefix={<HistoryOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Statut"
                    value={
                      selectedSplit.status === 'active'
                        ? 'Actif'
                        : selectedSplit.status === 'inactive'
                        ? 'Inactif'
                        : 'Hors service'
                    }
                    valueStyle={{
                      color:
                        selectedSplit.status === 'active'
                          ? '#52c41a'
                          : selectedSplit.status === 'inactive'
                          ? '#8c8c8c'
                          : '#ff4d4f',
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default SplitsPage;
